import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check, ChevronDown } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const PPFCalculator = () => {
  const [amtStr,    setAmtStr]    = useState('150000');
  const [duration,  setDuration]  = useState(15);
  const [rateStr,   setRateStr]   = useState('7.1');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied,    setCopied]    = useState(false);
  const [openFaq,   setOpenFaq]   = useState(null);

  const amount  = parseInt(amtStr)          || 0;
  const rate    = parseFloat(rateStr)        || 7.1;
  const sliderAmt   = Math.min(Math.max(amount, 500), 150000);
  const sliderRate  = Math.min(Math.max(rate, 1), 15);

  // PPF: annuity-due (deposit start of year), annual compounding
  const { totalInvested, totalInterest, maturity } = useMemo(() => {
    if (amount <= 0 || duration <= 0 || rate <= 0) return { totalInvested: 0, totalInterest: 0, maturity: 0 };
    const r = rate / 100;
    const M = Math.round(amount * (((Math.pow(1 + r, duration) - 1) / r) * (1 + r)));
    return { totalInvested: amount * duration, totalInterest: M - amount * duration, maturity: M };
  }, [amount, duration, rate]);

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  const fmtC = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
    return `₹${(n / 1000).toFixed(0)}K`;
  };
  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent('Calculate your PPF maturity with Kapitalised: kapitalised.com/calculators/ppf');
    if (platform === 'copy') { navigator.clipboard.writeText('https://kapitalised.com/calculators/ppf'); setCopied(true); setTimeout(() => setCopied(false), 2000); return; }
    const maps = { whatsapp: `https://wa.me/?text=${text}`, twitter: `https://twitter.com/intent/tweet?text=${text}` };
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  // Donut chart
  const R = 70, CX = 100, CY = 100;
  const circ    = 2 * Math.PI * R;
  const gap     = 5;
  const invPct  = maturity > 0 ? totalInvested / maturity : 0.5;
  const intPct  = maturity > 0 ? totalInterest / maturity : 0.5;
  const invDash = Math.max(invPct * circ - gap, 0);
  const intDash = Math.max(intPct * circ - gap, 0);

  const labelSt    = { fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' };
  const valueSt    = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right' };
  const suffixSt   = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' };
  const minMaxSt   = { fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' };
  const inputBoxSt = { display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' };

  const css = `
    .ppf-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .ppf-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .ppf-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .no-spin { -moz-appearance: textfield; }
    .ppf-card { padding: 36px; }
    .ppf-seo  { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    .ppf-two-col { display: flex; gap: 24px; align-items: flex-start; }
    .ppf-chart-row { display: flex; align-items: center; gap: 36px; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .hero-section { padding: 32px 40px 80px 40px !important; }
      .ppf-seo { padding: 48px 40px 64px 40px !important; }
      .ppf-card { padding: 28px !important; }
    }
    @media (max-width: 639px) {
      .ppf-two-col { flex-direction: column !important; }
      .ppf-chart-row { flex-direction: column !important; align-items: stretch !important; }
      .ppf-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .ppf-seo { padding: 32px 20px 48px 20px !important; }
    }
  `;

  const faqs = [
    { q: 'What is PPF and who should invest?', a: 'Public Provident Fund (PPF) is a government-backed savings scheme with a 15-year lock-in. It is ideal for risk-averse investors wanting guaranteed, tax-free returns. The EEE (Exempt-Exempt-Exempt) status means the investment, interest, and maturity are all tax-free under Section 80C.' },
    { q: 'What is the current PPF interest rate?', a: 'The PPF interest rate is set by the Government every quarter. It has remained at 7.1% p.a. since April 2020. Interest is calculated monthly on the minimum balance between the 5th and last day of each month, and credited at year end.' },
    { q: 'Can I invest more than ₹1.5 lakh in PPF?', a: 'No. The maximum annual investment in PPF is ₹1,50,000. Any amount beyond this will not earn interest and will not qualify for the Section 80C deduction.' },
    { q: 'Can I extend my PPF after 15 years?', a: 'Yes. After the initial 15-year tenure you can extend in blocks of 5 years — with or without fresh contributions. This is one of the best strategies to maximise compounding in a tax-free environment.' },
    { q: 'When should I deposit to maximise returns?', a: 'Deposit before the 5th of April each year for maximum annual interest. A lump-sum deposit at the start of April earns the full year\'s interest compared to spreading deposits throughout the year.' },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>
      <Navbar background="#1a4d3e" />

      {/* Hero */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>PPF Calculator</span>
        </div>

        {/* Title + Share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: 0, lineHeight: 1 }}>PPF Calculator</h1>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowShareMenu(!showShareMenu)} style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}>
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter',  label: 'X',         bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Two-col layout */}
        <div className="ppf-two-col">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ppf-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* Yearly Investment */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Yearly Investment</label>
                  <div style={inputBoxSt}>
                    <span style={suffixSt}>₹</span>
                    <input type="text" inputMode="numeric" className="no-spin" value={amtStr}
                      onChange={e => setAmtStr(e.target.value.replace(/[^0-9]/g, ''))}
                      style={{ ...valueSt, width: '90px' }} />
                  </div>
                </div>
                <input type="range" className="ppf-slider" min={500} max={150000} step={500} value={sliderAmt}
                  onChange={e => setAmtStr(e.target.value)}
                  style={{ background: sliderBg(sliderAmt, 500, 150000) }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '10px' }}>
                  <span style={minMaxSt}>₹500</span><span style={minMaxSt}>₹1.5L</span>
                </div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {[50000, 100000, 150000].map(v => {
                    const active = sliderAmt === v;
                    return <button key={v} onClick={() => setAmtStr(String(v))} style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>{v === 150000 ? '₹1.5L' : fmtC(v)}</button>;
                  })}
                </div>
              </div>

              {/* Duration */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Investment Duration</label>
                  <div style={inputBoxSt}>
                    <input type="text" inputMode="numeric" className="no-spin" value={duration}
                      onChange={e => { const v = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 15; setDuration(Math.min(Math.max(v, 15), 50)); }}
                      style={{ ...valueSt, width: '40px' }} />
                    <span style={suffixSt}>Yr</span>
                  </div>
                </div>
                <input type="range" className="ppf-slider" min={15} max={50} step={1} value={duration}
                  onChange={e => setDuration(parseInt(e.target.value))}
                  style={{ background: sliderBg(duration, 15, 50) }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '10px' }}>
                  <span style={minMaxSt}>15 Yr</span><span style={minMaxSt}>50 Yr</span>
                </div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {[15, 20, 25, 30].map(v => {
                    const active = duration === v;
                    return <button key={v} onClick={() => setDuration(v)} style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>{v} Yr</button>;
                  })}
                </div>
              </div>

              {/* Interest Rate */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Interest Rate (p.a.) <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 400 }}>— current: 7.1%</span></label>
                  <div style={inputBoxSt}>
                    <input type="text" inputMode="decimal" className="no-spin" value={rateStr}
                      onChange={e => setRateStr(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
                      onBlur={() => setRateStr(String(Math.min(Math.max(parseFloat(rateStr) || 7.1, 1), 15)))}
                      style={{ ...valueSt, width: '48px' }} />
                    <span style={suffixSt}>%</span>
                  </div>
                </div>
                <input type="range" className="ppf-slider" min={1} max={15} step={0.1} value={sliderRate}
                  onChange={e => setRateStr(e.target.value)}
                  style={{ background: sliderBg(sliderRate, 1, 15) }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={minMaxSt}>1%</span><span style={minMaxSt}>15%</span>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* Chart + Results */}
              <div className="ppf-chart-row">
                <div style={{ flexShrink: 0 }}>
                  <svg viewBox="0 0 200 200" width="180" height="180">
                    <g transform={`rotate(-90, ${CX}, ${CY})`}>
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="28" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#115e59" strokeWidth="28"
                        strokeDasharray={`${invDash} ${circ}`} strokeLinecap="butt" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#4ade80" strokeWidth="28"
                        strokeDasharray={`${intDash} ${circ}`} strokeDashoffset={-(invDash + gap)} strokeLinecap="butt" />
                    </g>
                    <text x={CX} y={CY - 6} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fill: '#9ca3af', fontWeight: 500 }}>Maturity</text>
                    <text x={CX} y={CY + 14} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fill: '#111827', fontWeight: 700 }}>{fmtC(maturity)}</text>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Total Invested',  value: totalInvested,  dot: '#115e59' },
                    { label: 'Interest Earned', value: totalInterest,  dot: '#4ade80' },
                  ].map(({ label, value, dot }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>{label}</span>
                      </div>
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 700, color: '#111827' }}>{fmt(value)}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '16px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#111827', fontWeight: 600 }}>Maturity Amount</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '22px', fontWeight: 800, color: '#115e59', letterSpacing: '-0.02em' }}>{fmt(maturity)}</span>
                  </div>
                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.5 }}>
                    100% tax-free · EEE status · Section 80C eligible
                  </p>
                </div>
              </div>

            </div>
          </div>

          <SmartCalculatorsSidebar currentSlug="ppf" />
        </div>
      </div>

      {/* FAQ */}
      <div className="ppf-seo">
        <h2 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '32px', color: '#004036', marginBottom: '24px' }}>Frequently Asked Questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map(({ q, a }, i) => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: openFaq === i ? '#f9fafb' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{q}</span>
                <ChevronDown size={16} color="#6b7280" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, marginLeft: '12px' }} />
              </button>
              {openFaq === i && (
                <div style={{ padding: '0 20px 16px 20px', background: '#f9fafb' }}>
                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PPFCalculator;
